import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { ShieldOff, CheckCircle2, ArrowRight, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function LockConfirmationPage() {
  const { token } = useParams();
  const { t } = useI18n();
  const [state, setState] = useState({ loading: true, ok: false, error: null, data: null });
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    api.post(`/trust-card/lock-via-link/${token}`)
      .then((r) => setState({ loading: false, ok: true, error: null, data: r.data }))
      .catch((e) => setState({
        loading: false,
        ok: false,
        error: formatApiError(e.response?.data?.detail, t.trustCard.lockedPage.invalid),
        data: null,
      }));
  }, [token, t.trustCard.lockedPage.invalid]);

  return (
    <div className="min-h-screen bg-ag-paper ag-paper-grain flex items-center justify-center p-6">
      <div className="max-w-lg w-full ag-card p-8 md:p-10 text-center" data-testid="lock-page">
        <div className={`h-16 w-16 rounded-full ${state.ok ? "bg-emerald-50" : "bg-red-50"} flex items-center justify-center mx-auto mb-5`}>
          {state.loading ? (
            <div className="h-6 w-6 border-2 border-slate-300 border-t-ag-ink rounded-full animate-spin" />
          ) : state.ok ? (
            <CheckCircle2 className="h-8 w-8 text-emerald-600" strokeWidth={1.4} />
          ) : (
            <AlertTriangle className="h-8 w-8 text-red-600" strokeWidth={1.4} />
          )}
        </div>

        {state.loading ? (
          <div className="text-sm text-slate-500">{t.common.loading}</div>
        ) : state.ok ? (
          <>
            <h1 className="font-serif text-4xl text-ag-ink mb-3">{t.trustCard.lockedPage.title}</h1>
            <p className="text-slate-600 mb-2">{t.trustCard.lockedPage.sub(state.data?.first_name)}</p>
            {state.data?.notfall_id && (
              <p className="text-xs text-slate-400 font-mono mt-1 mb-5">Notfall-ID: {state.data.notfall_id}</p>
            )}
            <div className="bg-slate-50 rounded-2xl p-5 text-left mt-6 mb-6">
              <div className="text-sm font-medium text-ag-ink mb-2">{t.trustCard.lockedPage.next}</div>
              <ul className="space-y-1.5">
                {t.trustCard.lockedPage.nextItems.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <ShieldOff className="h-3.5 w-3.5 text-emerald-600 mt-1 shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Button asChild className="bg-ag-ink hover:bg-slate-800 rounded-xl">
              <Link to="/vertrauenskarte" data-testid="lock-open-app">
                {t.trustCard.lockedPage.openApp} <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </>
        ) : (
          <>
            <h1 className="font-serif text-3xl text-ag-ink mb-3">{t.trustCard.lockedPage.invalid}</h1>
            <p className="text-slate-600">{state.error}</p>
          </>
        )}
      </div>
    </div>
  );
}
